<script setup lang="ts">
// The question a setup turn asks, with its clickable options (v3 `OrpheusSetupQuestion.vue`,
// KUR-2). Only the thread's latest question takes an answer; older ones stay as a record. 1–9 pick
// the option with that number while focus is inside the card, never inside its own-answer field.
import { computed, ref } from 'vue'

import type { SetupOption, SetupStep } from '../../models/orpheusSetup.types'
import { setupAnswerText } from '../../service/orpheusSetupParts'

import { useI18n } from '@/shared/i18n'
import { Button } from '@/shared/ui'

const props = withDefaults(
  defineProps<{
    step: SetupStep
    interactive?: boolean
    loading?: boolean
    /** Options that can't be picked, by value, with the sentence that says why. */
    unavailable?: Record<string, string>
    /** Off when the answer's own text already asks it, so the question doesn't read twice. */
    showQuestion?: boolean
  }>(),
  { interactive: true, loading: false, unavailable: () => ({}), showQuestion: true },
)
const emit = defineEmits<{ answer: [text: string] }>()
const { t } = useI18n()

const selected = ref<string[]>([])
const ownAnswer = ref('')
const canPick = computed(() => props.interactive && !props.loading)

function isSelected(option: SetupOption): boolean {
  return selected.value.includes(option.value)
}

function pick(option: SetupOption): void {
  if (!canPick.value || props.unavailable[option.value]) return
  if (!props.step.multi) {
    selected.value = [option.value]
    emit('answer', setupAnswerText([option]))
    return
  }
  selected.value = isSelected(option)
    ? selected.value.filter((value) => value !== option.value)
    : [...selected.value, option.value]
}

function pickByNumber(event: KeyboardEvent): void {
  if (event.target instanceof HTMLInputElement) return
  if (event.altKey || event.ctrlKey || event.metaKey || !/^[1-9]$/.test(event.key)) return
  const option = props.step.options[Number(event.key) - 1]
  if (!option) return
  event.preventDefault()
  pick(option)
}

function submitSelection(): void {
  if (!selected.value.length) return
  emit(
    'answer',
    setupAnswerText(props.step.options.filter((option) => selected.value.includes(option.value))),
  )
}

function sendOwnAnswer(): void {
  const answer = ownAnswer.value.trim()
  if (!answer) return
  ownAnswer.value = ''
  emit('answer', answer)
}
</script>

<template>
  <div
    class="orpheus-setup-question w-full overflow-hidden rounded-lg border border-border bg-surface"
    data-testid="orpheus-setup-question"
    @keydown="pickByNumber"
  >
    <p
      v-if="showQuestion && step.question"
      class="m-0 px-4 pt-3.5 text-sm font-semibold text-primary"
    >
      {{ step.question }}
    </p>

    <div
      v-if="step.options.length"
      class="flex flex-col gap-1.5 p-3"
      role="group"
      :aria-label="step.question || t('orpheus.setup.question.options')"
    >
      <button
        v-for="(option, index) in step.options"
        :key="option.value"
        type="button"
        :aria-pressed="isSelected(option) ? 'true' : 'false'"
        :disabled="!canPick || Boolean(unavailable[option.value])"
        class="grid w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-2.5 rounded-md border-0 px-3 py-2.5 text-left transition-colors"
        :class="[
          isSelected(option)
            ? 'bg-orpheus-soft ring-1 ring-inset ring-orpheus-muted'
            : 'bg-surface-sunken',
          canPick && !unavailable[option.value]
            ? 'cursor-pointer hover:bg-orpheus-soft'
            : 'cursor-not-allowed',
          unavailable[option.value] ? 'opacity-55' : '',
        ]"
        data-testid="orpheus-setup-option"
        @click="pick(option)"
      >
        <span>
          <span class="block text-sm font-semibold text-primary">{{ option.label }}</span>
          <span
            v-if="unavailable[option.value] || option.description"
            class="mt-0.5 block text-sm text-muted"
            data-testid="orpheus-setup-option-description"
          >
            {{ unavailable[option.value] || option.description }}
          </span>
        </span>
        <span
          class="pt-0.5 text-xs"
          :class="isSelected(option) ? 'text-primary' : 'text-subtle'"
          aria-hidden="true"
        >
          {{ isSelected(option) ? '✓' : index + 1 }}
        </span>
      </button>
    </div>

    <form v-if="canPick" class="px-3 pb-3" @submit.prevent="sendOwnAnswer">
      <input
        v-model="ownAnswer"
        type="text"
        :placeholder="t('orpheus.setup.question.ownAnswer')"
        :aria-label="t('orpheus.setup.question.ownAnswerLabel')"
        class="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-fg placeholder:text-subtle focus:border-primary-muted focus:outline-none"
        data-testid="orpheus-setup-own-answer"
      />
    </form>

    <div v-if="canPick && step.multi" class="flex justify-end px-3 pb-3">
      <Button
        size="sm"
        :disabled="!selected.length"
        data-testid="orpheus-setup-submit"
        @click="submitSelection"
      >
        {{ t('orpheus.setup.question.submit') }}
      </Button>
    </div>
  </div>
</template>
